<template>
  <secondary-header>
    <template #left>
      <breadcrumbs :breadcrumbs="breadcrumbs"></breadcrumbs>
      <template v-if="isFromDashboard">
        <a class="btn-link" :href="goBackToDashboardUrl">Go back to dashboard.</a>
      </template>
    </template>
    <template #right>
      <list-view-actions
        :list-view="selectedPublishedListView.listView">
      </list-view-actions>
      <button
        v-if="isNotOneStop && $can('exportCSV', selectedPublishedListView)"
        @click="showExportModal"
        class="btn blue"
      >
        Export
      </button>

      <confirmation-modal
        v-if="modalStateFor(vueModalID.confirmObjectModelAction).isVisible"
        :state="modalStateFor(vueModalID.confirmObjectModelAction)"
        :index="vueModalID.confirmObjectModelAction"
        @toggle-modal="handleCloseConfirmationModal"
        :overrideConfig="actionConfirmationModalConfig"
      >
      </confirmation-modal>

      <list-view-export-modal
        v-if="modalStateFor(vueModalID.exportListViewModal, ModalStatus.Confirmation).isVisible"
        :state="modalStateFor(vueModalID.exportListViewModal)"
        :config="exportConfig"
        :index="vueModalID.exportListViewModal"
        @toggle-modal="resetExportListViewModal"
        @tryExport="tryExport"
        @fail="goToFailure"
        @success="goToSuccess"
        @retry="goToExporting"
      ></list-view-export-modal>
    </template>
  </secondary-header>
</template>

<script lang="ts">
import Vue from "vue";
import { RouteHelper } from "../../../common/helpers/routeHelper";
import { Route } from "../../../common/enums/route";
import objectModelActionConfirmationMixin from "../mixins/objectModelActionConfirmationMixin";
import Breadcrumbs from "../../../common/components/breadcrumbs.vue";
import { ObjectModelDefaultActionType } from "~/enums/objectModelDefaultActionType";
import ObjectModelAction from "~/models/objectModelActions/objectModelAction";
import { mapState } from "vuex";
import ListViewExportModal from "./modal-list-view-export.vue";
import ListViewActions from "./list-view-actions.vue";

interface Config {
  title: string
  job_id: string
  channel_id: string
  started_at: string
}

export default Vue.extend({
  mixins: [objectModelActionConfirmationMixin],
  components: {
    Breadcrumbs,
    ListViewExportModal,
    ListViewActions,
  },
  data(): { exportConfig: Config } {
    return {
      exportConfig: { channel_id: null, job_id: null, title: null, started_at: null },
    }
  },
  computed: {
    showCreate(): boolean {
      return this.$can("createInstance", this.selectedPublishedListView, { action: this.createObjectModelAction });
    },
    createObjectModelAction(): ObjectModelAction {
      return this.selectedPublishedListView.published_object_model.objectModelDefaultActionsArray.find(
        (a) => a.default_action_type === ObjectModelDefaultActionType.Create
      );
    },
    newEntryPath(): string {
      const namedWildcardOptions = RouteHelper.namedWildcardOptionsFactory({
        object_model: this.selectedPublishedListView.object_model_id,
      });
      return RouteHelper.path({
        route: Route.objectModelEntriesNew,
        namedWildcardOptions,
        query: {
          from: "Reporting & Submissions Dashboard",
          from_list_view: this.selectedPublishedListView.list_view_id,
        },
      });
    },
    exportListViewPath(): string {
      return `export`;
    },
    isFromDashboard(): boolean {
      return this.$router.currentRoute.query['from_dashboard_id'];
    },
    goBackToDashboardUrl() {
      const dashboardId = this.$route.query.from_dashboard_id
      const route = (this.$route.query.from === 'DashboardPreview') ? Route.previewDashboard : Route.dashboard

      return RouteHelper.path({
        route,
        modelOrId: dashboardId
      })
    },
    ...mapState("breadcrumbStore", ["breadcrumbs"]),
  },
  methods: {
    tryCreate(): void {
      this.tryPerformAction({ action_client_id: this.createObjectModelAction.client_id });
    },
    showExportModal() {
      this.showModal(this.vueModalID.exportListViewModal);
    },
    async tryExport(exportType = "listView"): Promise<void> {
      let customFileName: string = null
      // file name convention when exporting records from an embedded LV (i.e. after clicking "View All" from an embedded LV in a DV)
      if (this.selectedPublishedDetailView && this.selectedPublishedDetailView.embeddedListViewTitle) {
        const dvHeaderTitle = this.selectedDetailView.headerContentElement.contentElementsArray[0].label_title
        const embeddedListViewTitle = this.selectedPublishedDetailView.embeddedListViewTitle
        customFileName = `${dvHeaderTitle} - ${embeddedListViewTitle}`
      }
      const response = await $.ajax({
        url: RouteHelper.path({
          route: Route.listView,
          modelOrId: this.selectedPublishedListView.list_view_id + "/export",
          query: {
            customFileName,
            exportType,
            filter: this.selectedPublishedListView.filterQuery,
            ...this.selectedPublishedListView.filterQuery
          },
        }),
        type: "GET",
      });

      if (response.success) {
        await this.$store.dispatch("appNotificationStore/checkForUnreadNotifications");
        this.exportConfig = {
          channel_id: 'JobChannel',
          job_id: response.job_id,
          title: this.selectedPublishedListView.title,
          started_at: new Date().toISOString(), // not exactly +job.started_at+ but close enough
        }
        this.goToExporting()
      } else {
        this.goToFailure()
      }
    },
    performSelectedAction(): void {
      const objectModelAction = this.selectedObjectModelAction;
      // Action can only be ObjectModelDefaultActionType.Create
      if (objectModelAction.default_action_type !== ObjectModelDefaultActionType.Create) return;
      window.location.href = this.newEntryPath;
    },
    goToExporting() {
      this.modalStateFor(this.vueModalID.exportListViewModal).status = 'exporting'
    },
    goToFailure() {
      this.modalStateFor(this.vueModalID.exportListViewModal).status = this.ModalStatus.Failure
    },
    goToSuccess() {
      this.modalStateFor(this.vueModalID.exportListViewModal).status = this.ModalStatus.Successful
    },
    resetExportListViewModal() {
      this.hideModal(this.vueModalID.exportListViewModal)
      this.modalStateFor(this.vueModalID.exportListViewModal).status = this.ModalStatus.Confirmation;
    },
  },
});
</script>
